import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase.config';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';

const SEND_AUTH_OTP_HTTP =
  'https://us-central1-go7-app-209e4.cloudfunctions.net/sendAuthOTPHttp';

type OtpSendResult = {
  success?: boolean;
  status?: string;
  messageId?: string;
  message?: string;
};

export class FirebaseAuthService {
  private pendingPhoneNumber: string | null = null;
  private pendingOTP: string | null = null;

  private assertOtpAccepted(data: OtpSendResult | null | undefined) {
    if (data?.status?.startsWith('REJECTED')) {
      const reason = data.status.replace(/^REJECTED_/, '').replace(/_/g, ' ').toLowerCase();
      throw new Error(
        data.status === 'REJECTED_NOT_ENOUGH_CREDITS'
          ? 'WhatsApp OTP service is out of credits. Please contact GO7 support.'
          : `WhatsApp OTP was rejected: ${reason}`
      );
    }
  }

  /** Prefer HTTP+CORS endpoint — callable preflight 503s show up as browser CORS errors. */
  private async sendOtpViaHttp(phoneNumber: string, otp: string): Promise<OtpSendResult> {
    const response = await fetch(SEND_AUTH_OTP_HTTP, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phoneNumber, otp }),
    });

    let payload: OtpSendResult & { error?: { message?: string } } = {};
    try {
      payload = (await response.json()) as typeof payload;
    } catch {
      payload = {};
    }

    if (!response.ok) {
      throw new Error(
        payload.message ||
          payload.error?.message ||
          `OTP service unavailable (${response.status})`
      );
    }

    return payload;
  }

  private async sendOtpViaCallable(phoneNumber: string, otp: string): Promise<OtpSendResult> {
    const functions = getFunctions(getApp(), 'us-central1');
    const sendAuthOTP = httpsCallable(functions, 'sendAuthOTP');
    const result = await sendAuthOTP({ phoneNumber, otp });
    return result.data as OtpSendResult;
  }

  // Send OTP via Firebase Function (Infobip)
  async sendOTP(phoneNumber: string, containerId?: string): Promise<boolean> {
    try {
      console.log('📱 Sending WhatsApp OTP via Infobip to:', phoneNumber);
      
      // Store phone number for verification later
      this.pendingPhoneNumber = phoneNumber;
      
      // Generate 6-digit OTP
      const otp = Math.floor(100000 + Math.random() * 900000).toString();
      this.pendingOTP = otp;

      let data: OtpSendResult;
      try {
        data = await this.sendOtpViaHttp(phoneNumber, otp);
      } catch (httpError) {
        console.warn('⚠️ sendAuthOTPHttp failed, falling back to callable:', httpError);
        data = await this.sendOtpViaCallable(phoneNumber, otp);
      }

      this.assertOtpAccepted(data);

      console.log('✅ Infobip WhatsApp OTP sent successfully:', data);
      return true;
    } catch (error: any) {
      console.error('❌ OTP sending failed:', error);
      const message = String(error?.message || '');
      if (/cors|failed to fetch|network|internal|503|500/i.test(message)) {
        throw new Error(
          'OTP service is temporarily unavailable. Please try again in a moment.'
        );
      }
      throw new Error(message || 'Failed to send OTP via WhatsApp');
    }
  }

  // Verify OTP and sign in
  async verifyOTP(phoneNumber: string, otp: string): Promise<User> {
    try {
      console.log('🔑 Verifying OTP for:', phoneNumber);
      console.log('📥 Entered OTP:', otp);
      console.log('💾 Stored OTP:', this.pendingOTP);
      console.log('📱 Stored Phone:', this.pendingPhoneNumber);

      // Verify OTP (we stored it when sending)
      if (!this.pendingOTP) {
        throw new Error('No OTP sent. Please request a new code.');
      }

      if (otp !== this.pendingOTP) {
        console.error('❌ OTP mismatch:', { entered: otp, stored: this.pendingOTP });
        throw new Error('Invalid OTP code');
      }

      if (phoneNumber !== this.pendingPhoneNumber) {
        console.error('❌ Phone mismatch:', { current: phoneNumber, stored: this.pendingPhoneNumber });
        throw new Error('Phone number mismatch');
      }

      console.log('✅ OTP verified, creating/signing in Firebase user...');

      // Create/sign in Firebase user with email/password
      const phoneDigits = phoneNumber.replace(/\D/g, '');
      const email = `${phoneDigits}@go7.rider`;
      const password = `go7_${phoneNumber}_secure`;

      console.log('📧 Firebase email:', email);

      try {
        // Try to sign in first
        console.log('🔐 Attempting sign in...');
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('✅ Sign in successful!');
        this.pendingOTP = null; // Clear OTP after successful verification
        this.pendingPhoneNumber = null;
        return userCredential.user;
      } catch (signInError: any) {
        console.log('⚠️ Sign in failed, attempting to create account...', signInError.code);
        // If user doesn't exist, create new account
        if (signInError.code === 'auth/user-not-found' || signInError.code === 'auth/invalid-credential') {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          console.log('✅ Account created successfully!');
          this.pendingOTP = null; // Clear OTP after successful verification
          this.pendingPhoneNumber = null;
          return userCredential.user;
        }
        throw signInError;
      }
    } catch (error: any) {
      console.error('❌ OTP verification failed:', error);
      throw new Error(error.message || 'Invalid OTP');
    }
  }

  // Continue with old verifyOTP for backward compatibility
  async sendOTPOld(phoneNumber: string, containerId?: string): Promise<boolean> {
    try {
      console.log('Sending WhatsApp OTP to:', phoneNumber);
      
      // Store phone number for verification later
      this.pendingPhoneNumber = phoneNumber;
      
      // Call your GO7 backend API
      const response = await fetch('https://go7-backend-production.up.railway.app/api/send-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phoneNumber })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send OTP');
      }

      const data = await response.json();
      console.log('OTP sent successfully!', data);
      return true;
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      throw new Error(error.message || 'Failed to send OTP');
    }
  }

  // Verify OTP code
  async verifyOTPWithPhone(phoneNumber: string, code: string): Promise<User> {
    try {
      if (!this.pendingPhoneNumber) {
        throw new Error('No pending phone verification. Send OTP first.');
      }

      // Verify OTP with your GO7 backend API
      const response = await fetch('https://go7-backend-production.up.railway.app/api/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phoneNumber: this.pendingPhoneNumber,
          otp: code
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid OTP');
      }

      const data = await response.json();
      console.log('OTP verified successfully!', data);

      // Create/sign in user with Firebase using phone number
      const email = `${this.pendingPhoneNumber.replace(/\D/g, '')}@go7.rider`;
      const password = `go7_${this.pendingPhoneNumber}_secure`;
      
      try {
        // Try to create new user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        this.pendingPhoneNumber = null;
        return userCredential.user;
      } catch (createError: any) {
        if (createError.code === 'auth/email-already-in-use') {
          // User exists, sign in instead
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          this.pendingPhoneNumber = null;
          return userCredential.user;
        }
        throw createError;
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw error;
    }
  }

  // Email/Password Sign Up
  async signUpWithEmail(email: string, password: string): Promise<User> {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  }

  // Email/Password Sign In
  async signInWithEmail(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  }

  // Sign Out
  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  }

  // Listen to auth state changes
  onAuthStateChange(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
  }

  // Get user token
  async getUserToken(): Promise<string | undefined> {
    const user = this.getCurrentUser();
    if (user) {
      return await user.getIdToken();
    }
    return undefined;
  }
}

export const authService = new FirebaseAuthService();
